import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = (process.env.PUBLIC_URL || '') + '/api';

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // If already logged in, redirect
  useEffect(() => {
    const token = localStorage.getItem('maxrank_token');
    if (token) {
      // Verify token is still valid
      fetch(API_BASE + '/user-auth/me', {
        headers: { 'Authorization': 'Bearer ' + token },
      })
        .then(r => { if (r.ok) return r.json(); throw new Error(); })
        .then(user => {
          if (onLogin) onLogin(user, token);
          navigate('/');
        })
        .catch(() => {
          localStorage.removeItem('maxrank_token');
          localStorage.removeItem('maxrank_user');
        });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(API_BASE + '/user-auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || 'Login failed');
        setLoading(false);
        return;
      }

      // Store token and user
      localStorage.setItem('maxrank_token', data.token);
      localStorage.setItem('maxrank_user', JSON.stringify(data.user));

      if (onLogin) onLogin(data.user, data.token);
      navigate('/');
    } catch (err) {
      setError('Connection error - check server');
      setLoading(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      {/* Background grid effect */}
      <div style={styles.gridBg} />

      {/* Floating ambient shapes */}
      <div style={styles.shape1} />
      <div style={styles.shape2} />
      <div style={styles.shape3} />

      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoWrap}>
          <div style={styles.logoMark}>DA</div>
          <div style={styles.logoText}>
            Daily AI
            <span style={styles.logoSub}>SEO & GEO Platform</span>
          </div>
        </div>

        {/* Title */}
        <div style={styles.title}>כניסה למערכת</div>
        <div style={styles.subtitle}>מערכת ניהול קידום אורגני ו-GEO</div>

        {/* Error */}
        {error && (
          <div style={styles.errorBox}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* Form */}
        <div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>אימייל</label>
            <div style={styles.inputWrap}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" style={styles.inputIcon}>
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                style={styles.input}
                autoFocus
                dir="ltr"
              />
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>סיסמה</label>
            <div style={styles.inputWrap}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" style={styles.inputIcon}>
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                style={styles.input}
                dir="ltr"
                onKeyDown={e => e.key === 'Enter' && handleSubmit(e)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.eyeBtn}
                title={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading || !email || !password}
            style={{
              ...styles.submitBtn,
              opacity: (loading || !email || !password) ? 0.5 : 1,
              cursor: (loading || !email || !password) ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? (
              <div style={styles.spinner} />
            ) : (
              <>
                <span>התחבר</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: 'scaleX(-1)' }}>
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <div>Daily AI - SEO & GEO Platform</div>
          <div style={styles.footerLinks}>
            <a href={(process.env.PUBLIC_URL || '') + "/legal/privacy.html"} style={styles.footerLink}>מדיניות פרטיות</a>
            <span style={{opacity:0.3}}>|</span>
            <a href={(process.env.PUBLIC_URL || '') + "/legal/terms.html"} style={styles.footerLink}>תנאי שימוש</a>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg)',
    position: 'relative',
    overflow: 'hidden',
    padding: '20px',
  },
  gridBg: {
    position: 'fixed',
    inset: 0,
    backgroundImage: 'linear-gradient(var(--grid-line) 1px, transparent 1px), linear-gradient(90deg, var(--grid-line) 1px, transparent 1px)',
    backgroundSize: '48px 48px',
    pointerEvents: 'none',
    zIndex: 0,
  },
  shape1: {
    position: 'fixed',
    top: '15%',
    right: '10%',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  shape2: {
    position: 'fixed',
    bottom: '20%',
    left: '5%',
    width: '250px',
    height: '250px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(168, 85, 247, 0.06) 0%, transparent 70%)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  shape3: {
    position: 'fixed',
    top: '60%',
    right: '60%',
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(34, 197, 94, 0.05) 0%, transparent 70%)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  card: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    maxWidth: '420px',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '16px',
    padding: '40px 36px 32px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.2), 0 0 0 1px var(--border)',
    animation: 'fadeIn 0.4s ease-out',
  },
  logoWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '28px',
    justifyContent: 'center',
  },
  logoMark: {
    width: '42px',
    height: '42px',
    background: 'linear-gradient(135deg, #3B82F6, #6366F1)',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: 800,
    color: '#fff',
    letterSpacing: '-0.5px',
    fontFamily: "'JetBrains Mono', monospace",
  },
  logoText: {
    fontSize: '20px',
    fontWeight: 700,
    color: 'var(--text)',
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.2,
  },
  logoSub: {
    fontSize: '11px',
    fontWeight: 400,
    color: 'var(--text-muted)',
    letterSpacing: '0.5px',
  },
  title: {
    fontSize: '22px',
    fontWeight: 700,
    color: 'var(--text)',
    textAlign: 'center',
    marginBottom: '6px',
  },
  subtitle: {
    fontSize: '13px',
    color: 'var(--text-muted)',
    textAlign: 'center',
    marginBottom: '28px',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    background: 'rgba(239, 68, 68, 0.08)',
    border: '1px solid rgba(239, 68, 68, 0.2)',
    borderRadius: '8px',
    color: '#EF4444',
    fontSize: '13px',
    marginBottom: '16px',
    direction: 'rtl',
  },
  fieldGroup: {
    marginBottom: '18px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    marginBottom: '6px',
  },
  inputWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute',
    right: '12px',
    pointerEvents: 'none',
    zIndex: 1,
  },
  input: {
    width: '100%',
    padding: '11px 42px 11px 42px',
    background: 'var(--bg)',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    color: 'var(--text)',
    fontSize: '14px',
    fontFamily: "'Rubik', 'Heebo', sans-serif",
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    direction: 'ltr',
    textAlign: 'left',
  },
  eyeBtn: {
    position: 'absolute',
    left: '10px',
    background: 'none',
    border: 'none',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'color 0.2s',
  },
  submitBtn: {
    width: '100%',
    padding: '12px',
    background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
    border: 'none',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '15px',
    fontWeight: 600,
    fontFamily: "'Rubik', 'Heebo', sans-serif",
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'opacity 0.2s, transform 0.1s',
    marginTop: '6px',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 0.6s linear infinite',
  },
  footer: {
    textAlign: 'center',
    fontSize: '11px',
    color: 'var(--text-muted)',
    marginTop: '24px',
    letterSpacing: '0.5px',
  },
  footerLinks: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '6px',
    fontSize: '11px',
  },
  footerLink: {
    color: 'var(--text-muted)',
    textDecoration: 'none',
    transition: 'color 0.2s',
  },
};

// Add keyframes for spinner
if (typeof document !== 'undefined' && !document.getElementById('login-keyframes')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'login-keyframes';
  styleSheet.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
    .login-input:focus { border-color: var(--accent) !important; box-shadow: 0 0 0 3px var(--accent-glow) !important; }
  `;
  document.head.appendChild(styleSheet);
}